import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { UserAccessField } from "@/components/UserAccessField";

const normRole = (r) => (r === "operator" ? "operador" : r);

export default function UsersPage() {
  const { user: currentUser, can } = useAuth();
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", role: "operador", password: "", permissions: null });
  const [form, setForm] = useState({ name: "", username: "", email: "", password: "", role: "operador", permissions: null });

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/users`, { withCredentials: true });
      setUsers(data);
    } catch { toast.error("Erro ao carregar usuarios"); }
    finally { setLoading(false); }
  }, []);

  const fetchRoles = useCallback(async () => {
    try {
      const [{ data: r }, { data: m }] = await Promise.all([
        axios.get(`${API}/roles`, { withCredentials: true }),
        axios.get(`${API}/permissions/modules`, { withCredentials: true }),
      ]);
      setRoles(r);
      setModules(m.modules);
    } catch { toast.error("Erro ao carregar setores"); }
  }, []);

  useEffect(() => { fetchUsers(); fetchRoles(); }, [fetchUsers, fetchRoles]);
  const roleName = (id) => roles.find((r) => r.id === normRole(id))?.name || id;
  const isAdmin = currentUser?.role === "admin";

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (!payload.permissions) delete payload.permissions;
      await axios.post(`${API}/users`, payload, { withCredentials: true });
      toast.success("Usuario criado");
      setDialogOpen(false);
      setForm({ name: "", username: "", email: "", password: "", role: "operador", permissions: null });
      fetchUsers(); fetchRoles();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao criar usuario"); }
  };

  const openEditDialog = (u) => {
    setEditingUser(u);
    setEditForm({ name: u.name, email: u.email || "", role: normRole(u.role), password: "", permissions: JSON.parse(JSON.stringify(u.permissions || {})) });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { name: editForm.name, email: editForm.email, role: editForm.role };
      if (editForm.role !== "admin" && editForm.permissions) payload.permissions = editForm.permissions;
      if (editForm.password) payload.password = editForm.password;
      await axios.put(`${API}/users/${editingUser.id}`, payload, { withCredentials: true });
      toast.success("Usuario atualizado");
      setEditDialogOpen(false);
      setEditingUser(null);
      fetchUsers(); fetchRoles();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao atualizar usuario"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Confirma exclusao do usuario?")) return;
    try {
      await axios.delete(`${API}/users/${id}`, { withCredentials: true });
      toast.success("Usuario removido");
      fetchUsers(); fetchRoles();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao remover"); }
  };

  return (
    <div className="space-y-6 animate-slide-in" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Usuarios</h1>
          <p className="text-sm text-muted-foreground mt-1">Gerenciamento de usuarios do sistema</p>
        </div>
      </div>

      {can("usuarios", "create") && (
        <div className="flex justify-end">
          <Button onClick={() => setDialogOpen(true)} data-testid="create-user-btn">
            <Plus className="h-4 w-4 mr-2" /> Novo Usuario
          </Button>
        </div>
      )}
      <Card className="border border-border shadow-none">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Usuario</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Setor</TableHead>
                <TableHead className="w-[100px]">Acoes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Carregando...</TableCell></TableRow>
              ) : users.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Nenhum usuario</TableCell></TableRow>
              ) : (
                users.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell className="font-mono text-sm">{u.username}</TableCell>
                    <TableCell className="text-sm">{u.email || "-"}</TableCell>
                    <TableCell>
                      <Badge className={u.role === "admin" ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground"} data-testid={`user-role-badge-${u.id}`}>
                        {roleName(u.role)}
                      </Badge>
                      {u.customized && <Badge className="ml-1 bg-amber-100 text-amber-800 text-[10px]" data-testid={`user-customized-badge-${u.id}`}>Personalizado</Badge>}
                    </TableCell>
                    <TableCell>
                      {u.id !== currentUser.id && (u.role !== "admin" || isAdmin) && (
                        <div className="flex gap-1">
                          {can("usuarios", "edit") && (
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditDialog(u)} data-testid={`edit-user-${u.id}`}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          {can("usuarios", "delete") && (
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(u.id)} data-testid={`delete-user-${u.id}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Novo Usuario</DialogTitle>
            <DialogDescription>Crie uma conta, escolha o setor e ajuste as permissoes se precisar</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Nome *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="user-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Usuario (login) *</Label>
              <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} required placeholder="Ex: joao.silva" data-testid="user-username-input" />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" />
            </div>
            <div className="space-y-2">
              <Label>Senha *</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="user-password-input" />
            </div>
            <UserAccessField role={form.role} permissions={form.permissions} onChange={(v) => setForm({ ...form, ...v })} roles={roles} modules={modules} isAdmin={isAdmin} testId="user-role-select" />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" data-testid="user-submit-btn">Criar Usuario</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Usuario</DialogTitle>
            <DialogDescription>Altere as informacoes de {editingUser?.name}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Nome *</Label>
              <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} required data-testid="edit-user-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} data-testid="edit-user-email-input" />
            </div>
            <UserAccessField role={editForm.role} permissions={editForm.permissions} onChange={(v) => setEditForm({ ...editForm, ...v })} roles={roles} modules={modules} isAdmin={isAdmin} testId="edit-user-role-select" />
            <div className="space-y-2">
              <Label>Nova Senha (deixe em branco para manter)</Label>
              <Input type="password" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} placeholder="••••••" data-testid="edit-user-password-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" data-testid="edit-user-submit-btn">Salvar Alteracoes</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
